import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable, ReportColumn } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ─── Types ─────────────────────────────────────────────────────────────────────

type GroupBy = 'item' | 'invoice' | 'period';

interface ProfitMarginTotals {
  revenue: number;
  cost: number;
  gross_margin: number;
  margin_pct: number;
}

interface ProfitMarginData {
  group: GroupBy;
  rows: Record<string, unknown>[];
  totals: ProfitMarginTotals;
}

interface ProfitMarginResponse {
  data: ProfitMarginData;
}

// ─── Column definitions ─────────────────────────────────────────────────────────

const MONEY_COLS: ReportColumn[] = [
  { key: 'revenue', header: 'Revenue', money: true },
  { key: 'cost', header: 'Cost', money: true },
  { key: 'gross_margin', header: 'Gross Margin', money: true },
  { key: 'margin_pct', header: 'Margin %' },
];

const COLUMNS_BY_GROUP: Record<GroupBy, ReportColumn[]> = {
  item: [{ key: 'item_name', header: 'Item' }, { key: 'qty', header: 'Qty' }, ...MONEY_COLS],
  invoice: [
    { key: 'number', header: 'Number' },
    { key: 'customer_name', header: 'Customer' },
    ...MONEY_COLS,
  ],
  period: [...MONEY_COLS],
};

// ─── Helper: format margin_pct field ──────────────────────────────────────────

function formatMarginPct(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map((r) => ({
    ...r,
    margin_pct:
      r.margin_pct !== null && r.margin_pct !== undefined
        ? `${Number(r.margin_pct).toFixed(1)}%`
        : '—',
  }));
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function ProfitMarginReport() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [allDates, setAllDates] = useState(false);
  const [group, setGroup] = useState<GroupBy>('item');

  // "All dates" overrides From/To with an empty range (all-time on the backend)
  // without clearing the stored values, so toggling it off restores prior dates.
  const effFrom = allDates ? '' : from;
  const effTo = allDates ? '' : to;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'profit-margin', effFrom, effTo, group],
    queryFn: () => {
      const params = new URLSearchParams({ group });
      if (effFrom) params.set('from', effFrom);
      if (effTo) params.set('to', effTo);
      return apiFetch<ProfitMarginResponse>(`/api/reports/profit-margin?${params.toString()}`);
    },
  });

  const report = data?.data;
  const activeGroup: GroupBy = report?.group ?? group;
  const columns = COLUMNS_BY_GROUP[activeGroup];

  const rows = formatMarginPct(report?.rows ?? []);

  const totalsRow =
    report && activeGroup !== 'period'
      ? {
          ...(activeGroup === 'item' ? { item_name: 'Total' } : { number: 'Total' }),
          revenue: report.totals.revenue,
          cost: report.totals.cost,
          gross_margin: report.totals.gross_margin,
          margin_pct: `${Number(report.totals.margin_pct).toFixed(1)}%`,
        }
      : undefined;

  const keyField =
    activeGroup === 'item' ? 'item_id' : activeGroup === 'invoice' ? 'estimate_id' : undefined;

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Profit & Margin</h1>
        <ExportButtons
          endpoint="/api/reports/profit-margin"
          params={{ from: effFrom || undefined, to: effTo || undefined, group }}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Label htmlFor="pm-from" className="text-sm text-muted-foreground">
            From
          </Label>
          <Input
            id="pm-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            disabled={allDates}
            className={`w-36 h-8 text-sm${allDates ? ' opacity-50' : ''}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="pm-to" className="text-sm text-muted-foreground">
            To
          </Label>
          <Input
            id="pm-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={allDates}
            className={`w-36 h-8 text-sm${allDates ? ' opacity-50' : ''}`}
          />
        </div>

        {/* All-dates toggle — overrides From/To with all-time */}
        <div className="flex items-center gap-2">
          <input
            id="pm-all-dates"
            type="checkbox"
            checked={allDates}
            onChange={(e) => setAllDates(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 accent-primary"
          />
          <Label htmlFor="pm-all-dates" className="cursor-pointer text-sm text-muted-foreground">
            All dates
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor="pm-group" className="text-sm text-muted-foreground">
            Group by
          </Label>
          <select
            id="pm-group"
            value={group}
            onChange={(e) => setGroup(e.target.value as GroupBy)}
            className="h-8 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="item">Item</option>
            <option value="invoice">Invoice</option>
            <option value="period">Period</option>
          </select>
        </div>
        {!allDates && (from || to) && (
          <button
            type="button"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
          >
            Clear dates
          </button>
        )}
      </div>

      {/* Loading / error */}
      {isLoading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && (
        <ReportTable columns={columns} rows={rows} totalsRow={totalsRow} keyField={keyField} />
      )}
    </div>
  );
}
